import { useRowSelect } from '@table-library/react-table-library/select';
import { Action, State } from '@table-library/react-table-library/types/common';
import { useQueryClient } from '@tanstack/react-query';
import { Button, TableItem } from 'components/atoms';
import { FormInput, LabeledStatus, MagellanPopups } from 'components/molecules';
import LabeledProductType from 'components/molecules/labeledStatus/LabeledProductType';
import PopupForm from 'components/molecules/popup/form/PopupForm';
import { QueryFilter, Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { FormikHelpers } from 'formik';
import { useComponentVisible, useHasPermission, useQueryToast } from 'hooks';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import i18next from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiMusicNotesPlus, PiUserCircleDashed, PiWarning } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { PopupActions } from 'reactjs-popup/dist/types';
import {
  useGetMusicProductsDigitalList,
  usePutMassReplaceDuplicateArtists,
} from 'services/music-product-digital';
import {
  DIGITAL_PRODUCT_TYPE,
  RELEASE_TYPE,
} from 'services/music-product-digital/types/enums';
import { IMassReplaceDuplicateArtistsParams } from 'services/music-product-digital/types/mutation';
import {
  IMusicProductDigitalResponse,
  IMusicProductDigitalSearch,
} from 'services/music-product-digital/types/query';
import { STATUS } from 'services/types';
import { useSearchById, useSetSearchById } from 'store/state';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { magellanColumns } from 'types/components/table/table.types';
import {
  booleanSelectOptions,
  useEnumToSelectOptions,
} from 'utils/components/inputs/select';
import { formatDate } from 'utils/global/date.utils';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';
import { getNodes } from 'utils/table/nodes.utils';
import { boolean, object } from 'yup';

enum MUSIC_PRODUCT_DIGITAL_COLUMNS {
  productType,
  title,
  release,
  ean,
  // externalIds,
  copyrights,
  releaseStatus,
}

const musicProductDigitalSearchInitialValues: IMusicProductDigitalSearch = {
  ean: '',
  artistName: '',
  artistNickname: '',
  isrc: '',
  assetId: '',
  title: '',
  status: undefined,
  productType: undefined,
  hasContracts: undefined,
  deliveryDiscrepancy: undefined,
};

const MASS_REPLACE_TOAST_MAX_LENGTH = 120;

const massReplaceDuplicateArtistsInitialValues: IMassReplaceDuplicateArtistsParams =
  {
    resendDeliveryUpdate: false,
  };

const massReplaceDuplicateArtistsValidator = object().shape({
  resendDeliveryUpdate: boolean().required(i18next.t('formik-error.required')),
});

const MusicProductsDigital = () => {
  usePermissionGuard(
    'DigitalMusicProductPermissionsLIST**',
    '/',
    true,
    'music-product-digital'
  );

  const { t } = useTranslation();
  const navigate = useNavigate();
  const hasPermission = useHasPermission();
  const queryClient = useQueryClient();

  const massReplaceDuplicateArtists = usePutMassReplaceDuplicateArtists();
  const { success: successToast, dismiss: dismissToast } = useQueryToast(
    'usePutMassReplaceDuplicateArtists'
  );

  const [showResultPopup, setShowResultPopup] = useState(false);
  const [resultMessage, setResultMessage] = useState('');

  const columnsVisibility = useTableColumnsVisibility(
    'MusicProductsDigital5',
    MUSIC_PRODUCT_DIGITAL_COLUMNS
  );

  const i18nextPrefix = 'music-product-digital.list';

  const searchById = useSearchById();
  const setSearchById = useSetSearchById();
  const filters =
    searchById[columnsVisibility.tableId] ||
    musicProductDigitalSearchInitialValues;

  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false, true, columnsVisibility.tableId);

  const { data, isLoading, fetchNextPage, isFetchingNextPage } =
    useGetMusicProductsDigitalList({ ...filters, itemCount: 25 });

  const tableData = getNodes<IMusicProductDigitalResponse>(data?.pages.flat());

  const onSelectChange = (action: Action, state: State) => {
    if (!hasDetailPermission) {
      return;
    }

    navigate(`${state.id ?? state.ids[state.ids.length - 1]}/contracts`, {
      relative: 'path',
    });
  };

  const select = useRowSelect(tableData, {
    onChange: onSelectChange,
  });

  const hasDetailPermission = hasPermission(
    'DigitalMusicProductPermissionsLIST**',
    16
  );
  const tableSelect = hasDetailPermission ? select : undefined;

  const { getSelectOptions } = useEnumToSelectOptions();

  const showMassReplaceResult = (message: string) => {
    if (message.length > MASS_REPLACE_TOAST_MAX_LENGTH) {
      dismissToast();
      setResultMessage(message);
      setShowResultPopup(true);
      return;
    }

    successToast(message);
  };

  const onMassReplaceDuplicateArtists = async (
    values: IMassReplaceDuplicateArtistsParams,
    helpers: FormikHelpers<IMassReplaceDuplicateArtistsParams>,
    actions: PopupActions | null
  ) => {
    await massReplaceDuplicateArtists.mutateAsync(values, {
      onSuccess: data => {
        helpers.resetForm();
        actions?.close();
        queryClient.invalidateQueries({
          queryKey: ['music-product-digital-list'],
        });
        showMassReplaceResult(
          t('music-product-digital.mass-replace-duplicates.success', {
            updatedDigitalProductsCount: data.updatedDigitalProductsCount,
            updatedTracksCount: data.updatedTracksCount,
            updatedVideosCount: data.updatedVideosCount,
            deliveryUpdatesSentCount: data.deliveryUpdatesSentCount,
          })
        );
      },
      onError: error => onErrorShowAllErrorsToast(error),
    });
  };

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={t(`${i18nextPrefix}.title`)}
          path=''
          hideBackButton
          topbarContent={
            <>
              <div className='flex'>
                <QueryFilter.ButtonOpener
                  i18nextPrefix={i18nextPrefix}
                  onClick={handleComponentVisible}
                  variant={BUTTON_VARIANT.default}
                  isClear
                />
                <Table.CustomColumnsButton
                  columns={MUSIC_PRODUCT_DIGITAL_COLUMNS}
                  isClear
                  visibility={columnsVisibility}
                  i18nextPrefix={i18nextPrefix}
                />
              </div>
              <div className='flex gap-2'>
                {hasPermission('DigitalMusicProductPermissionsLIST**', 1) && (
                  <PopupForm<IMassReplaceDuplicateArtistsParams>
                    trigger={
                      <Button variant={BUTTON_VARIANT.default}>
                        <Button.Icon icon={<PiUserCircleDashed />} />
                        <Button.Text
                          text={t(
                            'music-product-digital.mass-replace-duplicates.button'
                          )}
                        />
                      </Button>
                    }
                    header={t(
                      'music-product-digital.mass-replace-duplicates.header'
                    )}
                    initialValues={massReplaceDuplicateArtistsInitialValues}
                    validationSchema={massReplaceDuplicateArtistsValidator}
                    onSubmit={onMassReplaceDuplicateArtists}
                    submitButton={
                      <Button
                        type='submit'
                        variant={BUTTON_VARIANT.submit}
                        isLoading={massReplaceDuplicateArtists.isPending}>
                        <Button.Text text={t('button.replace-duplicates')} />
                      </Button>
                    }>
                    <FormInput.Select
                      name='resendDeliveryUpdate'
                      label={t(
                        'music-product-digital.mass-replace-duplicates.resend-delivery-update'
                      )}
                      options={booleanSelectOptions}
                    />
                  </PopupForm>
                )}
                {hasPermission('DigitalMusicProductPermissionsLIST**', 1) && (
                  <Button
                    variant={BUTTON_VARIANT.submit}
                    onClick={() =>
                      navigate('create/tracks', { replace: false })
                    }>
                    <Button.Text
                      text={t('button.create-what', {
                        what: t(`${i18nextPrefix}.what`),
                      })}
                    />
                    <Button.Icon icon={<PiMusicNotesPlus />} />
                  </Button>
                )}
              </div>
            </>
          }>
          <QueryFilter<IMusicProductDigitalSearch>
            i18nextPrefix={i18nextPrefix}
            initialValues={filters}>
            <>
              <QueryFilter.Form<IMusicProductDigitalSearch>
                ref={ref}
                isOpen={isComponentVisible}
                onDebouncedFormChange={values =>
                  setSearchById(columnsVisibility.tableId, values)
                }>
                <QueryFilter.InputsGrid>
                  <FormInput.Select
                    name='productType'
                    label={t('input.label.productType')}
                    options={getSelectOptions(
                      DIGITAL_PRODUCT_TYPE,
                      'enum.DIGITAL_PRODUCT_TYPE'
                    )}
                  />
                  <FormInput.Select
                    name='status'
                    label={t('input.label.status')}
                    options={getSelectOptions(STATUS, 'enum.STATUS')}
                  />
                  <FormInput.Text name='title' label={t('input.label.title')} />
                  <FormInput.Text
                    name='artistNickname'
                    label={t('input.label.artistNickname')}
                  />
                  <FormInput.Text
                    name='artistName'
                    label={t('input.label.artistName')}
                  />
                  <FormInput.Text name='ean' label={t('input.label.ean')} />
                  <FormInput.Text name='isrc' label='ISRC' />
                  <FormInput.Text
                    name='assetId'
                    label={t('input.label.assetId')}
                  />
                  <FormInput.Select
                    name='deliveryDiscrepancy'
                    label={t('input.label.deliveryDiscrepancy')}
                    options={booleanSelectOptions}
                  />
                  <FormInput.Select
                    name='hasDeliveryToAccept'
                    label={t('input.label.hasDeliveryToAccept')}
                    options={booleanSelectOptions}
                  />
                  <FormInput.Select
                    name='hasDuplicatedArtist'
                    label={t('input.label.hasDuplicatedArtist')}
                    options={booleanSelectOptions}
                  />
                  <FormInput.Select
                    name='hasContracts'
                    label={t('input.label.hasContracts')}
                    options={booleanSelectOptions}
                  />
                </QueryFilter.InputsGrid>
              </QueryFilter.Form>
              <QueryFilter.Labels
                isFormOpen={isComponentVisible}
                customLabel={data =>
                  data.key === 'status'
                    ? t(`enum.STATUS.${STATUS[data.value as STATUS]}`)
                    : data.key === 'productType'
                    ? t(
                        `enum.DIGITAL_PRODUCT_TYPE.${
                          DIGITAL_PRODUCT_TYPE[
                            data.value as DIGITAL_PRODUCT_TYPE
                          ]
                        }`
                      )
                    : data.key === 'deliveryDiscrepancy'
                    ? data.value !== undefined
                      ? `${t('input.label.deliveryDiscrepancy')}: ${
                          data.value === true
                            ? t('input.option.yes')
                            : t('input.option.no')
                        } `
                      : undefined
                    : data.key === 'hasDeliveryToAccept'
                    ? data.value !== undefined
                      ? `${t('input.label.hasDeliveryToAccept')}: ${
                          data.value === true
                            ? t('input.option.yes')
                            : t('input.option.no')
                        } `
                      : undefined
                    : data.key === 'hasDuplicatedArtist'
                    ? data.value !== undefined
                      ? `${t('input.label.hasDuplicatedArtist')}: ${
                          data.value === true
                            ? t('input.option.yes')
                            : t('input.option.no')
                        } `
                      : undefined
                    : data.key === 'hasContracts'
                    ? data.value !== undefined
                      ? `${t('input.label.hasContracts')}: ${
                          data.value === true
                            ? t('input.option.yes')
                            : t('input.option.no')
                        } `
                      : undefined
                    : `${data.value}`
                }
              />
            </>
          </QueryFilter>
        </TopBar>
      }>
      <Table
        id={columnsVisibility.tableId}
        select={tableSelect}
        columns={magellanColumns<IMusicProductDigitalResponse>([
          {
            label: `${t(`${i18nextPrefix}.table.productType`)}`,
            renderCell: ({ productType }) => (
              <TableItem>
                <LabeledProductType status={productType} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(
              MUSIC_PRODUCT_DIGITAL_COLUMNS.productType
            ),
          },
          {
            label: `${t(`${i18nextPrefix}.table.title`)}`,
            renderCell: ({ productImagePreviewLink, mainTitle, id }) => (
              <TableItem>
                <div className='flex flex-row items-center gap-4'>
                  <TableItem.Image
                    url={productImagePreviewLink}
                    alt={mainTitle.title}
                  />
                  <div className='flex flex-col'>
                    <TableItem.Text text={mainTitle.title} />
                    {mainTitle?.version && (
                      <TableItem.Text
                        text={mainTitle?.version}
                        isSupplementary
                      />
                    )}
                  </div>
                </div>
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(MUSIC_PRODUCT_DIGITAL_COLUMNS.title),
            minWidth: 200,
          },
          {
            label: `${t(`${i18nextPrefix}.table.release`)}`,
            renderCell: ({ releaseType, releaseDate }) => (
              <TableItem>
                <TableItem.Text
                  text={
                    releaseType
                      ? t(`enum.RELEASE_TYPE.${RELEASE_TYPE[releaseType]}`)
                      : undefined
                  }
                />
                <TableItem.Text
                  text={releaseDate ? formatDate(releaseDate) : undefined}
                  isSupplementary
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(MUSIC_PRODUCT_DIGITAL_COLUMNS.release),
          },
          {
            label: `${t(`${i18nextPrefix}.table.ean`)}`,
            renderCell: ({ ean }) => (
              <TableItem>
                <TableItem.Text text={ean ?? undefined} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(MUSIC_PRODUCT_DIGITAL_COLUMNS.ean),
          },
          {
            label: t(`${i18nextPrefix}.table.copyrights`),
            renderCell: ({ cLine, pLine }) => (
              <TableItem>
                <TableItem.Text
                  isSupplementary
                  text={
                    cLine?.name && cLine?.year
                      ? `© ${cLine.year} ${cLine.name}`
                      : undefined
                  }
                />
                <TableItem.Text
                  isSupplementary
                  text={
                    pLine?.name && pLine?.year
                      ? `℗ ${pLine.year} ${pLine.name}`
                      : undefined
                  }
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(
              MUSIC_PRODUCT_DIGITAL_COLUMNS.copyrights
            ),
          },
          {
            label: t(`${i18nextPrefix}.table.releaseStatus`),
            renderCell: ({ releaseStatus, mainTitle, id }) => (
              <TableItem justifyPosition='center'>
                <LabeledStatus status={releaseStatus} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(
              MUSIC_PRODUCT_DIGITAL_COLUMNS.releaseStatus
            ),
            minWidth: 140,
          },
          {
            label: '',
            minWidth: 56,
            renderCell: ({
              hasDeliveryDiscrepancy,
              deliveryHasErrors,
              hasDuplicatedArtist,
              hasDeliveryToAccept,
              mainTitle,
              id,
            }) => {
              const hasAnyError =
                hasDeliveryDiscrepancy ||
                deliveryHasErrors ||
                hasDuplicatedArtist ||
                hasDeliveryToAccept;
              const navigateTo = hasDuplicatedArtist
                ? `${id}/artists`
                : `${id}/delivery`;

              return (
                <TableItem justifyPosition='end'>
                  <div className='flex '>
                    {hasAnyError && (
                      <Button
                        onlyIcon
                        isClear
                        variant={
                          deliveryHasErrors || hasDuplicatedArtist
                            ? BUTTON_VARIANT.remove
                            : BUTTON_VARIANT.warning
                        }
                        onClick={() => navigate(navigateTo)}>
                        <Button.Description description={t('button.info')} />

                        <Button.Icon icon={<PiWarning />} />
                      </Button>
                    )}
                  </div>
                </TableItem>
              );
            },
          },
        ])}
        loading={isLoading || isFetchingNextPage}
        onFullyScroll={fetchNextPage}
        data={tableData}
        displayMode
      />
      <MagellanPopups.WarningPopup
        open={showResultPopup}
        onClose={() => setShowResultPopup(false)}
        title={t('music-product-digital.mass-replace-duplicates.result-title')}
        message={resultMessage}
      />
    </TopbarPage>
  );
};

export default MusicProductsDigital;
